import {
  type AccountSettings,
  accountSettingsSchema,
  ApiError,
} from "@fapinstructor/common";
import {
  Box,
  Button,
  RangeSlider,
  Skeleton,
  Slider,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useUpdateAccountSettings } from "@/api/fapinstructor/account/update-account-settings";
import { handleApiError } from "@/api/api";
import { createFileRoute } from "@tanstack/react-router";
import { Page } from "@/components/page";
import { accountSettingsQueryOptions } from "@/api/fapinstructor/account/get-account-settings";
import { PendingPageTitle } from "@/components/skeletons/pending-page-title";

export const Route = createFileRoute(
  "/_authenticated/_subscribed/_main/settings",
)({
  loader: (opts) =>
    opts.context.queryClient.ensureQueryData(accountSettingsQueryOptions()),
  component: SettingsRouteComponent,
  pendingComponent: PendingComponent,
});

function SettingsRouteComponent() {
  const accountSettings = Route.useLoaderData();

  return (
    <Page title="Settings">
      <Title>Settings</Title>
      <Text>Your user specific settings.</Text>
      <Box maw="700px">
        <SettingsForm initialValues={accountSettings} />
      </Box>
    </Page>
  );
}

type SettingsFormProps = {
  initialValues: AccountSettings;
};

function SettingsForm({ initialValues }: SettingsFormProps) {
  const updateAccountSettingsMutation = useUpdateAccountSettings();
  const form = useForm<AccountSettings>({
    validate: zodResolver(accountSettingsSchema),
    initialValues: initialValues,
  });

  return (
    <form
      onSubmit={form.onSubmit(async (settings) => {
        try {
          await updateAccountSettingsMutation.mutateAsync(settings);
          form.resetDirty();
          form.resetTouched();

          notifications.show({
            title: "Successfully Saved",
            message: "Your settings have been saved.",
            color: "green",
            autoClose: 3000,
          });
        } catch (error) {
          if (error instanceof ApiError) {
            const errors = await handleApiError(error);
            form.setErrors(errors);
            return;
          }
          notifications.show({
            title: "Something went wrong",
            message: "An error occured while trying to save your settings.",
            color: "red",
            autoClose: 10000,
          });
        }
      })}
    >
      <Stack mt="md" gap="xl">
        <Stack gap="md">
          <Stack gap={0}>
            <Text size="sm">Stroke Tempo (min-max)</Text>
            <Text size="xs" c="dimmed">
              The minimum and maximum speeds that you will be stroking at.
            </Text>
          </Stack>
          <RangeSlider
            min={0}
            max={360}
            minRange={50}
            w="100%"
            size="lg"
            label={(value) => `${(value / 60).toFixed(1)} strokes per second`}
            marks={[
              { value: 0, label: "0" },
              { value: 60, label: "1" },
              { value: 120, label: "2" },
              { value: 180, label: "3" },
              { value: 240, label: "4" },
              { value: 300, label: "5" },
              { value: 360, label: "6" },
            ]}
            {...form.getInputProps("tempo")}
          />
          <Text size="xs" c="red">
            {form.getInputProps("tempo").error}
          </Text>
        </Stack>
        <Stack gap="md">
          <Stack gap={0}>
            <Text size="sm">Stroke Beat Pattern Tempo (min-max)</Text>
            <Text size="xs" c="dimmed">
              The range of tempos of when the beat patterns will become enabled.
            </Text>
          </Stack>
          <RangeSlider
            w="100%"
            min={0}
            max={360}
            minRange={1}
            label={(value) => `${value} tempo`}
            key={form.key("beatPatternTempo")}
            {...form.getInputProps("beatPatternTempo")}
          />

          <Text size="xs" c="red">
            {form.getInputProps("beatPatternTempo").error}
          </Text>
        </Stack>
        <Stack gap="md">
          <Stack gap={0}>
            <Text size="sm">Stroke Tempo Edging Percentage</Text>
            <Text size="xs" c="dimmed">
              While edging, control how fast you will be stroking relative to
              your max stroke tempo.
            </Text>
          </Stack>
          <Slider
            min={0}
            max={100}
            w="100%"
            size="lg"
            label={(value) => `${value}%`}
            marks={[
              { value: 0, label: "0%" },
              { value: 20, label: "20%" },
              { value: 40, label: "40%" },
              { value: 60, label: "60%" },
              { value: 80, label: "80%" },
              { value: 100, label: "100%" },
            ]}
            {...form.getInputProps("edgingTempoPercent")}
          />
          <Text size="xs" c="red">
            {form.getInputProps("edgingTempoPercent").error}
          </Text>
        </Stack>
        <Stack gap="xs">
          <Stack gap={0}>
            <Text size="sm">Instructor Typing Speed</Text>
            <Text size="xs" c="dimmed">
              Control how fast the instructor types.
            </Text>
          </Stack>
          <Slider
            min={0}
            max={100}
            w="100%"
            size="lg"
            label={(value) =>
              `${(value / 60).toFixed(1)} characters per second`
            }
            marks={[
              { value: 0, label: "Instant" },
              { value: 15, label: "Fast" },
              { value: 30, label: "Medium" },
              { value: 50, label: "Slow" },
              { value: 100, label: "Very Slow" },
            ]}
            {...form.getInputProps("chat.speed")}
          />
          <Text size="xs" c="red">
            {form.getInputProps("chat.speed").error}
          </Text>
        </Stack>
        <Box>
          <Button disabled={!form.isDirty()} type="submit">
            Save
          </Button>
        </Box>
      </Stack>
    </form>
  );
}

function PendingSettingsGroup() {
  return (
    <Stack>
      <Skeleton height={20} w={200} />
      <Skeleton height={20} w={400} />
      <Skeleton height={40} w="100%" />
    </Stack>
  );
}

function PendingComponent() {
  return (
    <Stack gap="xl">
      <PendingPageTitle />
      <PendingSettingsGroup />
      <PendingSettingsGroup />
      <PendingSettingsGroup />
    </Stack>
  );
}
